import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { CampaignProductScope } from '@/constants/CampaignProductScope';
import { campaignSteps } from '@/constants/CampaignSteps.jsx';
import { validateCriteria, validateTiers, hasValidImage, isEmptyValue } from '@/helpers/marketing/campaignValidation';

export const useCampaignStepCompletion = activeStep => {
    const promotionType = useWatch({ name: 'promotionType' });
    const productScope = useWatch({ name: 'productScope' });
    const brandIds = useWatch({ name: 'brandIds' });
    const categoryIds = useWatch({ name: 'categoryIds' });
    const productId = useWatch({ name: 'productId' });
    const criteria = useWatch({ name: 'criteria' });
    const discount = useWatch({ name: 'discount' });
    const startDate = useWatch({ name: 'startDate' });
    const endDate = useWatch({ name: 'endDate' });
    const file = useWatch({ name: 'file' });
    const image = useWatch({ name: 'image' });

    const values = useWatch();

    const isDefinitionComplete = useMemo(() => {
        if (isEmptyValue(promotionType) || isEmptyValue(productScope)) return false;
        if (productScope === CampaignProductScope.SINGLE) {
            return !isEmptyValue(productId);
        }
        if (productScope === CampaignProductScope.GROUP) {
            const hasBrands = Array.isArray(brandIds) && brandIds.length > 0;
            const hasCategories = Array.isArray(categoryIds) && categoryIds.length > 0;
            return hasBrands || hasCategories;
        }
        if (productScope === CampaignProductScope.ALL) {
            return true;
        }
        return false;
    }, [promotionType, productScope, brandIds, categoryIds, productId]);

    const isCriteriaComplete = useMemo(() => validateCriteria(criteria), [criteria]);

    const isDiscountComplete = useMemo(() => {
        const discountData = discount || {};
        const type = discountData?.type;
        if (type === 'fixed') {
            return !isEmptyValue(discountData?.fixedPercent);
        }
        if (type === 'depositBased') {
            return validateTiers(discountData?.depositTiers, ['limit', 'discount']);
        }
        if (type === 'quantityBased') {
            return validateTiers(discountData?.quantityTiers, ['limit', 'value']);
        }
        return false;
    }, [discount]);

    const isEmissionComplete = useMemo(() => {
        return !isEmptyValue(startDate) && !isEmptyValue(endDate) && hasValidImage(image, file);
    }, [startDate, endDate, file, image]);

    const isStepCompleted = index => {
        switch (index) {
            case 0:
                return isDefinitionComplete;
            case 1:
                return isCriteriaComplete;
            case 2:
                return isDiscountComplete;
            case 3:
                return isEmissionComplete;
            case 4:
                return true;
            default:
                return false;
        }
    };

    const isCurrentCompleted = isStepCompleted(activeStep);
    const lastIndex = campaignSteps.length - 1;
    const allPriorStepsCompleted = targetIndex => {
        const upto = Math.min(targetIndex, lastIndex - 1);
        for (let i = 0; i <= upto; i += 1) {
            if (!isStepCompleted(i)) return false;
        }
        return true;
    };

    const allStepsCompleted = allPriorStepsCompleted(lastIndex - 1);

    const isStepEnabled = index => {
        if (index <= activeStep) return true;
        if (allStepsCompleted) return true;
        return allPriorStepsCompleted(index - 1);
    };

    return { isStepCompleted, isStepEnabled, isCurrentCompleted, allStepsCompleted, values, steps: campaignSteps };
};

export default useCampaignStepCompletion;
